import { Reference, StoreObject, TypePolicies } from '@apollo/client';
import { User } from '@sentry/react';
import { isNil } from 'lodash';

import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { NO_GIFTING_SENTINEL } from '@/modules/gifting/proposal/designer/form';
import { localFederalEstateTaxPercentFormattedVar } from '@/modules/irs/useIRSConstants';
import {
  EstateWaterfall,
  EstateWaterfallLoanValueHistory,
  EstateWaterfallLocalShowSummaryTileDetailsArgs,
  UserLocalMostRecentlyViewedEntityMapArgs,
  UserLocalMostRecentlyViewedWaterfallArgs,
} from '@/types/schema';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import {
  mostRecentlyViewedEntityMapVar,
  mostRecentlyViewedWaterfallVar,
  showWaterfallSummaryTileDetailsVar,
} from '../reactiveVars';

export const handwrittenTypePolicies: TypePolicies = {
  // Preview IDs are document-local, not canonical graph record identities.
  KGLoadingNode: { keyFields: false },
  KGLoadingRelationship: { keyFields: false },
  // Question IDs are unique within a message group, not across messages.
  GroupedQuestion: {
    keyFields: false,
  },
  // Diagram IDs identify elements inside a snapshot, not shared entities.
  // Revisions retain IDs but must not rewrite another card's cached snapshot.
  Diagram: { keyFields: false },
  DiagramContent: { keyFields: false },
  DiagramMessageContent: { keyFields: false },
  DiagramNode: { keyFields: false },
  DiagramEdge: { keyFields: false },
  DiagramGroup: { keyFields: false },
  DiagramAnnotation: { keyFields: false },
  DiagramPosition: { keyFields: false },
  // these types also expose an ID, but the ID references a different
  // entity, testamentary entity, client profile, etc., and we do not
  // want to cache this result to that ID
  BeneficiaryReportEntityBreakdown: {
    keyFields: ['_cacheKey'],
  },
  BeneficiaryReportEntityAccess: {
    keyFields: ['_cacheKey'],
  },
  ClientProfile: {
    keyFields: ['id'],
  },
  Entity: {
    keyFields: ['id'],
  },
  EstateWaterfall: {
    keyFields: ['id'],
    fields: {
      visualizationWithProjections: {
        // This field has no id, so we need to specify how to merge it.
        // We simply merge the objects together.
        merge(
          existing: StoreObject | Reference,
          incoming: StoreObject | Reference,
          { mergeObjects }
        ) {
          // If we encounter an incoming visualizationWithProjections, we merge the existing and incoming objects.
          // This means Query A can include keys omited in Query B and we will merge them together.
          return mergeObjects(existing, incoming);
        },
      },
      localShowSummaryTileDetails: {
        read(
          _,
          {
            args,
          }: { args: EstateWaterfallLocalShowSummaryTileDetailsArgs | null }
        ): EstateWaterfall['localShowSummaryTileDetails'] {
          const waterfallId = args?.where?.waterfallId;
          const localVar = showWaterfallSummaryTileDetailsVar();
          const key = `${LOCAL_STORAGE_KEYS.WATERFALL_SUMMARY_TILE_DETAILS}_${waterfallId}`;
          const varFromLocalStorage =
            localStorageBooleanSerializer.deserializer(
              localStorage.getItem(key) ?? 'true'
            );
          return isNil(localVar) ? varFromLocalStorage : localVar;
        },
      },
    },
  },
  /*******************
   * @start Do not normalize types block
   * @description This block is used to prevent Apollo from normalizing certain types. These types
   * do not have an id field and are difficult to key, so we are explicitly telling Apollo to not
   * normalize them.
   */
  EstateValueMetrics: {
    keyFields: false,
  },
  ClientOverallPerformanceReport: {
    keyFields: false,
  },
  HouseholdMetrics: {
    keyFields: false,
  },
  HouseholdAssetsSummary: {
    keyFields: false,
  },
  EstateWaterfallTransfer: {
    keyFields: false,
  },
  FamilyTreeNodeSettings: {
    keyFields: false,
  },
  FamilyTreeSettings: {
    keyFields: false,
  },
  Household: {
    fields: {
      // EntityConnection has no ID. Different queries fetch different subsets of
      // its fields (e.g. totalCount in one, only edges in another), so we merge
      // to avoid losing cached fields. keyArgs separates filtered vs. unfiltered calls.
      entities: {
        keyArgs: ['where'],
        merge: true,
      },
      // estateValueMetrics returns HouseholdMetrics which has no ID. Different queries
      // fetch different subsets of its fields, so we merge to avoid losing cached fields.
      estateValueMetrics: {
        merge: true,
      },
      // Ensure possiblePrimaryClients are always sorted consistently to prevent
      // unstable ordering that can cause UI flickering and inconsistent behavior
      possiblePrimaryClients: {
        merge(
          existing: unknown,
          incoming: ReadonlyArray<Reference | StoreObject> | null | undefined,
          { readField }
        ) {
          if (!incoming) return existing;
          // Sort by createdAt for consistent creation order, with id as fallback for stability
          // This matches the ordering used in other queries like ClientPeopleListViewTable
          return [...incoming].sort((a, b) => {
            const aCreatedAt = readField<string>('createdAt', a);
            const bCreatedAt = readField<string>('createdAt', b);
            const createdAtComparison =
              new Date(aCreatedAt ?? 0).getTime() -
              new Date(bCreatedAt ?? 0).getTime();
            if (createdAtComparison !== 0) return createdAtComparison;

            const aId = readField<string>('id', a) ?? '';
            const bId = readField<string>('id', b) ?? '';
            return aId.localeCompare(bId);
          });
        },
      },
    },
  },
  EntityGraphNodeConfiguration: {
    keyFields: false,
  },
  GiftingProposalProjections: {
    keyFields: false,
  },
  // Fixture descriptors for the internal e360 corpus panel. They carry no id, so
  // they stay inline under the no-argument Query field that returns them, which
  // makes that field replace-on-write. Only the corpus panel selects it. A second
  // query selecting a different subset would drop the first query's fields; give
  // this type keyFields: ['family'] if that ever happens, because merge: true
  // cannot help a list field (mergeObjects throws on arrays).
  SeedableE360FixtureFamily: {
    keyFields: false,
  },
  // BeneficiaryDesignations and its primary/contingent groups are embedded (no stable
  // id). EntityPageBasics and GetBeneficiaryDesignations fetch this field with different
  // subsets, so we merge to avoid each write dropping the other's fields (which sends
  // the two cache-and-network queries into a mutual-refetch loop).
  BeneficiaryDesignations: {
    keyFields: false,
    merge: true,
  },
  // The server live-converts legacy dispositionScenarios into primary/contingent
  // BeneficiaryDesignationGroups, which come back with an empty `id`. Both tiers would
  // otherwise normalize to the same cache key and clobber each other. Keep them inline
  // under their parent BeneficiaryDesignations, and merge so different field subsets coexist.
  // TODO: remove once dispositionScenarios are migrated to real BDGs and the server stops
  // live-converting (these groups will then carry stable ids).
  BeneficiaryDesignationGroup: {
    keyFields: false,
    merge: true,
  },
  IRSConstants: {
    // IRS constants have no ID field and should not be normalized
    keyFields: false,
    fields: {
      localFederalEstateTaxPercentFormatted: {
        read(): string {
          // Read the percent string from the reactive var
          return localFederalEstateTaxPercentFormattedVar();
        },
      },
    },
  },
  /******************* @end Do not normalize types block */
  Query: {
    fields: {
      irsConstants: {
        // Cache IRS constants based on the 'at' (time) parameter
        // Different time values may have different IRS constants (they change yearly)
        keyArgs: ['at'],
      },
    },
  },
  // We must use a combination of id and afterDeath to avoid weird caching issues where apollo transposes incorrect data between items in a list
  // Context: https://withluminary.slack.com/archives/C05BPQS8CG4/p1692291675704939?thread_ts=1692220364.855759&cid=C05BPQS8CG4
  EstateWaterfallVizNode: {
    keyFields: ['_cacheKey'],
  },
  EstateWaterfallLoan: {
    keyFields: ['id', 'pseudoID'],
    fields: {
      valueHistory: {
        keyArgs: ['year'],
        merge(_existing, incoming) {
          // we always get all years, so we can just replace the existing with the incoming
          return incoming as EstateWaterfallLoanValueHistory[];
        },
      },
    },
  },
  EstateWaterfallLoanValueHistory: {
    keyFields: false,
  },
  EstateWaterfallNodeValueHistory: {
    keyFields: false,
  },
  TaxDetail: {
    keyFields: ['_cacheKey'],
  },
  TaxSummary: {
    keyFields: ['_cacheKey'],
  },
  EstateWaterfallViz: {
    keyFields: ['_cacheKey'],
  },
  // ['to', ['id', 'afterDeath']] --> translate to nested fields: { to: { id: string, afterDeath: AfterDeath } }
  // https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
  EstateWaterfallVizEdge: {
    keyFields: ['to', ['_cacheKey'], 'from', ['_cacheKey']],
  },
  InterestRateSummary: {
    // Identify an interest rate summary by its month and year
    keyFields: ['currentMonth', ['monthYearDisplay']],
  },
  PresentationSystemPrompt: {
    // No `id` field; the registry key is stable and unique per prompt.
    keyFields: ['key'],
  },
  GiftingProposalScenario: {
    fields: {
      /**
       * @description flag if the scenario is the baseline, no-gifting scenario;
       * always call this alongside `isDefault` in order to capture the system-defined default
       *
       * This check is repeated in GiftDesignerModelScenariosForm.utils, getGiftScenariosFromQueryData
       * for stability
       */
      isBaseline: {
        read(_, { readField }): boolean {
          const isDefault = readField<boolean>('isDefault') || false;
          const displayName = readField<string>('displayName') || '';
          return isDefault || displayName === NO_GIFTING_SENTINEL;
        },
      },
    },
  },
  User: {
    fields: {
      localMostRecentlyViewedWaterfall: {
        read(
          _,
          { args }: { args: UserLocalMostRecentlyViewedWaterfallArgs | null }
        ): User['localMostRecentlyViewedWaterfall'] {
          const householdId = args?.where?.householdId;
          if (!householdId) {
            return {
              id: null,
            };
          }

          // Read the reactive variable here to trigger the read function
          mostRecentlyViewedWaterfallVar();

          const key = `${LOCAL_STORAGE_KEYS.LAST_VIEWED_WATERFALL}_${householdId}`;
          const waterfallIdFromLocalStorage = localStorage.getItem(key);

          return {
            id: waterfallIdFromLocalStorage,
          };
        },
      },
      localMostRecentlyViewedEntityMap: {
        read(
          _,
          { args }: { args: UserLocalMostRecentlyViewedEntityMapArgs | null }
        ): User['localMostRecentlyViewedEntityMap'] {
          const householdId = args?.where?.householdId;
          const entityMapVar = mostRecentlyViewedEntityMapVar();

          const key = `${LOCAL_STORAGE_KEYS.LAST_VIEWED_ENTITY_MAP}_${householdId}`;
          const entityMapIdFromLocalStorage = localStorage.getItem(key);

          const id = entityMapVar
            ? entityMapVar.id
            : entityMapIdFromLocalStorage;
          return { id };
        },
      },
    },
  },
  TenantInformation: {
    fields: {
      trialEndDate: {
        // Ensure trialEndDate is stored as an ISO string in the cache.
        // this is weird, but because trialEndDate is fetched through SSR, we actually force it
        // to be a string in the cache because non-primitive value blow up in SSR.
        // so, we need to convert it back to a date here.
        read(dateValue: unknown) {
          if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return dateValue;
        },
        // When writing to the cache, convert Date to string
        merge(_, incoming) {
          if (incoming instanceof Date) {
            return incoming.toISOString();
          }
          return incoming as null;
        },
      },
    },
  },
};
